import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface LightboxImage {
  url: string;
  caption?: string;
  respondent_index?: number;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ImageLightbox({ images, initialIndex = 0, open, onOpenChange }: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const goNext = () => setCurrentIndex((i) => (i + 1) % images.length);
  const goPrev = () => setCurrentIndex((i) => (i - 1 + images.length) % images.length);

  // E1 / audit F-1: keyboard navigation. ArrowLeft/Right cycle, Escape
  // closes the dialog. Bound to window only while the lightbox is open.
  useEffect(() => {
    if (!open || images.length === 0) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // goNext/goPrev are referentially stable enough for this hook —
    // they only depend on images.length which is in the deps already.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, images.length, onOpenChange]);

  // E1 / audit F-1: don't open the dialog if there are no images at all —
  // would render a blank black overlay with nothing to look at.
  if (images.length === 0) return null;

  const current = images[currentIndex];
  if (!current) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-full p-0 bg-black/95 border-none">
        <div className="relative flex flex-col items-center">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 z-10 text-white/70 hover:text-white"
            aria-label="Close lightbox"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="relative w-full flex items-center justify-center min-h-[60vh]">
            {images.length > 1 && (
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-3 z-10 text-white/70 hover:text-white bg-black/30 rounded-full p-2"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}

            <img
              src={current.url}
              alt={
                current.caption ||
                `Respondent ${current.respondent_index ?? '?'} image`
              }
              className="max-h-[80vh] max-w-full object-contain"
            />

            {images.length > 1 && (
              <button
                type="button"
                onClick={goNext}
                className="absolute right-3 z-10 text-white/70 hover:text-white bg-black/30 rounded-full p-2"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>

          <div className="p-4 text-center">
            {current.caption && <p className="text-white/80 text-sm">{current.caption}</p>}
            {current.respondent_index !== undefined && (
              <p className="text-white/50 text-xs mt-1">Respondent #{current.respondent_index}</p>
            )}
            {images.length > 1 && (
              <p className="text-white/40 text-xs mt-2">
                {currentIndex + 1} / {images.length}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImageLightbox;
